import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Library, Layers, RotateCcw, MessageSquareText, BarChart3, Settings as SettingsIcon,
  Search, Sun, Moon, LogOut, GraduationCap,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/library", label: "Библиотека", icon: Library, testid: "nav-library" },
  { to: "/vocabulary", label: "Словарь", icon: Layers, testid: "nav-vocabulary" },
  { to: "/review", label: "Повторение", icon: RotateCcw, testid: "nav-review" },
  { to: "/practice", label: "AI практика", icon: MessageSquareText, testid: "nav-practice" },
  { to: "/statistics", label: "Статистика", icon: BarChart3, testid: "nav-statistics" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const themeInitialized = useRef(false);

  useEffect(() => {
    if (user && user.settings?.theme && !themeInitialized.current) {
      themeInitialized.current = true;
      setTheme(user.settings.theme);
    }
  }, [user, setTheme]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <aside className="w-16 lg:w-56 border-r border-border flex flex-col shrink-0 bg-card transition-all duration-200">
        <div className="h-16 flex items-center justify-center lg:justify-start lg:px-5 gap-2 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <GraduationCap className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="hidden lg:block font-heading font-bold text-lg tracking-tight">EasyEnglish</span>
        </div>

        <nav className="flex-1 py-4 flex flex-col gap-1 px-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 justify-center lg:justify-start",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="hidden lg:block">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-2 pb-4">
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 justify-center lg:justify-start",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )
            }
          >
            <SettingsIcon className="w-5 h-5 shrink-0" />
            <span className="hidden lg:block">Настройки</span>
          </NavLink>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0 bg-background">
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="open-search-button"
            className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/60 hover:bg-muted rounded-lg px-3 py-2 w-56 lg:w-80 transition-colors duration-200"
          >
            <Search className="w-4 h-4" />
            <span>Поиск...</span>
            <kbd className="ml-auto hidden lg:inline text-xs bg-background border border-border rounded px-1.5 py-0.5">Ctrl K</kbd>
          </button>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              data-testid="theme-toggle-button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings-item">
                  <SettingsIcon className="w-4 h-4 mr-2" /> Настройки
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout-item">
                  <LogOut className="w-4 h-4 mr-2" /> Выйти
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
